"use client";
import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Accordion, Button, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo, useEffect } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MarketLocalePopup from './MarketLocalePopup';
import { saveProductMapping } from "../../store/slices/saveProductMapping";
import { getConfig } from '../../store/slices/getConfig';

//  Updated initial field
const initialField = { market: "", catlog: "", pimChannel: "", pimCurrency: "" };


const validateMappings = (values) => {
  const errors = {};

  const mappings = values.mappings || [];

  // No rows → valid
  if (mappings.length === 0) {
    return errors;
  }

  // Exactly one row
  if (mappings.length === 1) {
    const { market, catlog, pimChannel, pimCurrency } = mappings[0];

    // ALL EMPTY → VALID
    if (!market && !catlog && !pimChannel && !pimCurrency) {
      return errors;
    }

    // One is filled, so both are required
    if (!market || !catlog || !pimChannel || !pimCurrency) {
      errors.mappings = [{}];

      if (!market) {
        errors.mappings[0].market =
          "Markets is required";
      }

      if (!catlog) {
        errors.mappings[0].catlog =
          "Catlog is required";
      }

      if (!pimChannel) {
        errors.mappings[0].pimChannel =
          "PIM Channel is required";
      }

      if (!pimCurrency) {
        errors.mappings[0].pimCurrency =
          "PIM Currency is required";
      }

      return errors;
    }

    return errors;
  }

  // Multiple rows
  const mappingErrors = [];

  mappings.forEach((mapping, index) => {
    const rowErrors = {};

    if (!mapping.market) {
        rowErrors.market =
          "Markets is required";
      }

      if (!mapping.catlog) {
        rowErrors.catlog =
          "Catlog is required";
      }

      if (!mapping.pimChannel) {
        rowErrors.pimChannel =
          "PIM Channel is required";
      }

      if (!mapping.pimCurrency) {
        rowErrors.pimCurrency =
          "PIM Currency is required";
      }

    mappingErrors[index] = rowErrors;
  });

  // Only add mappings errors if there is actually an error
  if (
    mappingErrors.some(
      (row) => Object.keys(row).length > 0
    )
  ) {
    errors.mappings = mappingErrors;
  }

  return errors;
};


const parsePIMCatalogData = (data = []) => {
  const pimChannelOptions = data.map(({ code, displayName }) => ({
    code,
    label: displayName,
  }));

  const pimlocaleOptions = data.reduce((acc, { code, associatedLanguage = [] }) => {
    acc[code] = associatedLanguage.map((language) => ({
      code: language,
      label: language,
    }));

    return acc;
  }, {});

  const pimCurrencyOptions = data.reduce((acc, { code, currencies = [] }) => {
    acc[code] = currencies.map((currency) => ({
      code: currency,
      label: currency,
    }));

    return acc;
  }, {});

  return {
    pimChannelOptions,
    pimlocaleOptions,
    pimCurrencyOptions,
  };
};


const parseShopifyMarketData = (data = []) => {
  const shopifyMarketOptions = data.map(({ name }) => ({
    code: name,
    label: name,
  }));

  const shopifyCatalogOptions = data.reduce(
    (acc, { name, catalogs = [] }) => {
      acc[name] = catalogs.map((catalog) => ({
        code: catalog,
        label: catalog,
      }));

      return acc;
    },
    {}
  );

  const shopifyLocaleOptions = data.reduce(
    (acc, { name, locale = [] }) => {
      acc[name] = locale
        .filter(({ published }) => published)
        .map(({ locale: localeCode }) => ({
          code: localeCode,
          label: localeCode,
        }));

      return acc;
    },
    {}
  );

  return {
    shopifyMarketOptions,
    shopifyCatalogOptions,
    shopifyLocaleOptions,
  };
};


function MarketConfiguration({showToast}) {
  const dispatch = useDispatch();
  // const channels = useSelector((state) => state.pimChannels.data);
  const channels = useSelector(state => state.filtersOptions?.data?.pimChannels);
  const markets = useSelector((state)=> state.shopifyMarkets.data);
  const savedData = useSelector((state) => state.config.data);
  const configId = useSelector((state) => state.saveCreds.configId);
  const storeDomain = useSelector(state => state.config?.clientStoreHash);
  const [marketLocaleIndex, setMarketLocaleIndex]= useState(null);
  const {
  pimChannelOptions,
  pimlocaleOptions,
  pimCurrencyOptions,
} = parsePIMCatalogData(channels);
  const {shopifyMarketOptions, shopifyCatalogOptions, shopifyLocaleOptions} = parseShopifyMarketData(markets);

  const formik = useFormik({
    initialValues: {
      mappings: [initialField],
    },
    validate: validateMappings,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async(values) => {
      const payload = parseMarketMappingData(values);
      const req = {
        attributeMapping: {
        ...(savedData?.attributeMapping || {}),
            markets: payload.markets
        },
        struConfigId: configId || savedData?.struConfigId
      }
        await dispatch(saveProductMapping(req)).unwrap();
        await dispatch(getConfig(storeDomain)).unwrap();
        showToast("saveattributeMapping");
    },
  });

    const parseMarketMappingData = (formikValues) => {
    const channelMapping = {};

    formikValues?.mappings?.forEach(
      ({ market, catlog, pimChannel, pimCurrency }) => {
        // Ignore empty rows
        if (!market || !catlog || !pimChannel) return;

        // Get locale mapping from localStorage using catalog as key
        const savedData = localStorage.getItem(`${storeDomain+'|'+catlog}`);

        let languageMapping = {};

        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);

            languageMapping = (parsedData?.mappings || []).reduce(
              (acc, { pimlocale, shopifylocale }) => {
                if (pimlocale && shopifylocale) {
                  acc[pimlocale] = shopifylocale;
                }

                return acc;
              },
              {}
            );
          } catch (error) {
            console.error(
              `Failed to parse localStorage data for ${catlog}`,
              error
            );
          }
        }

        localStorage.removeItem(`${storeDomain+'|'+catlog}`);

        const isDeleted = localStorage.getItem(`${storeDomain+'|'+catlog}`) === null;

        channelMapping[pimChannel] = {
          marketName: market,
          languageMapping,
          currency: pimCurrency,
          catalog: catlog,
        };
      }
    );

    return {
      markets: {
        channelMapping,
      },
    };
  };


useEffect(() => {
  const channelMapping =
    savedData?.attributeMapping?.markets?.channelMapping;

    if (
    !channelMapping ||
    Object.keys(channelMapping).length === 0){
    formik.setFieldValue("mappings", [initialField]);
    return;
  }

  const mappings = Object.entries(channelMapping).map(
    ([pimChannel, marketData]) => {
      const {
        marketName,
        catalog,
        currency,
        languageMapping = {},
      } = marketData;

      const localeMappings = {
        mappings: Object.entries(languageMapping).map(
          ([pimlocale, shopifylocale]) => ({
            pimlocale,
            shopifylocale,
          })
        ),
      };

      // Save market catlog as localStorage key
      localStorage.setItem(
        `${storeDomain+'|'+catalog}`,
        JSON.stringify(localeMappings)
      );

      return {
        market: marketName || "",
        catlog: catalog || "",
        pimChannel: pimChannel || "",
        pimCurrency: currency || "",
      };
    }
  );

  formik.setFieldValue("mappings", mappings);
}, [savedData?.attributeMapping?.markets]);

  
  const { values, errors, touched, handleChange, handleSubmit } = formik;

  useEffect(() => {
    values?.mappings?.forEach((mapping, index) => {
      // If Market is empty, clear Catalog
      if (!mapping?.market && mapping?.catlog) {
        localStorage.removeItem(`${storeDomain+'|'+mapping.catlog}`)
        formik.setFieldValue(`mappings[${index}].catlog`, "");
      }

      // If PIM Channel is empty, clear PIM Currency
      if (!mapping?.pimChannel && mapping?.pimCurrency) {
        formik.setFieldValue(`mappings[${index}].pimCurrency`, "");
      }
    });
  }, [values.mappings]);
  
    const handleAddFields = useCallback(() => {
      const updatedMappings = [...values.mappings, initialField];
      formik.setFieldValue("mappings", updatedMappings);
    }, [values.mappings, formik]);
  
  const handleRemoveFields = useCallback(
    (index,catlog) => {
      const updatedMappings = values?.mappings?.filter((_, i) => i !== index);
      if(catlog){
        localStorage.removeItem(`${storeDomain+'|'+catlog}`);
      }
      formik.setFieldValue("mappings", updatedMappings);
    },
    [values.mappings, formik]
  );

  // Memoize the last index
  const lastIndex = useMemo(
    () => values.mappings.length - 1,
    [values.mappings.length]
  );

  // const filteringOptions = useCallback(
  //   (options = [], currentIndex, key) => {
  //     if (!Array.isArray(options)) return [];
  
  //     const selectedValues = formik.values.mappings
  //       .map((item, index) => {
  //         if (index === currentIndex) return null;
  //         return item?.[key];
  //       })
  //       .filter(Boolean);
  
  //     return options.filter(
  //       (option) => !selectedValues.includes(option.code)
  //     );
  //   },
  //   [formik.values.mappings]
  // );

  const filteringOptions = useCallback(
    (options = [], currentIndex, key) => {
      if (!Array.isArray(options)) return [];

      const selectedValues = formik.values?.mappings
        ?.filter((_, index) => index !== currentIndex)
        ?.map((item) => item?.[key])
        ?.filter(Boolean);

      return options.filter(
        (option) => !selectedValues?.includes?.(option.code)
      );
    },
    [formik.values.mappings]
  );

  return (
    <div className="mt-4">
      <Form onSubmit={handleSubmit}>
        <Accordion.Item eventKey="5" className="my-1">
          <Accordion.Header>
            Market Mapping
          </Accordion.Header>
          <Accordion.Body className="p-4">
            <div className="bg-light p-4 rounded mb-10 mapping_component">
              {/* <Row className="border-bottom pb-3 mb-3 align-items-center">
                <Col sm={12}>
                  <h6 className="fw-bold m-0">Pre-existing Products</h6>
                </Col>
              </Row> */}
              <Row className="market-mapping-row align-items-center">
                <Col sm={2} className="market-column market-header">
                  <h6 className="fw-bold mb-4">Market</h6>
                </Col>
                <Col sm={2} className="market-column market-header">
                  <h6 className="fw-bold mb-4 ">Catalog</h6>
                </Col>
                <Col sm={2} className="market-column market-header">
                  <h6 className="fw-bold mb-4">PIM Channel</h6>
                </Col>
                <Col sm={2} className="market-column market-header">
                  <h6 className="fw-bold mb-4">Locale</h6>
                </Col>
                <Col sm={2} className="market-column market-header">
                  <h6 className="fw-bold mb-4">PIM Currency</h6>
                </Col>
                <Col sm={1} xs="auto">
                  
                </Col>
              </Row>
              {values.mappings.map((inputField, index) => (
                <React.Fragment key={index}>
                <Row className="mb-3">
                {/* assetsfamily Dropdown */}
                <Col sm={2} className="market-column">
                  <Form.Group controlId={`mappings.${index}.market`}>
                    <Form.Select
                      name={`mappings[${index}].market`}
                      value={inputField?.market}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.market &&
                        touched.mappings?.[index]?.market
                      }
                    >
                      <option value="">Select the Market</option>
                      {filteringOptions(shopifyMarketOptions,index,"market")?.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.market}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col sm={2} className="market-column">
                  <Form.Group controlId={`mappings.${index}.catlog`}>
                    <Form.Select
                      name={`mappings[${index}].catlog`}
                      value={inputField?.catlog}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.catlog &&
                        touched.mappings?.[index]?.catlog
                      }
                    >
                      <option value="">Select the Catalog</option>
                      {filteringOptions(shopifyCatalogOptions[inputField?.market],index,"catlog")?.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.catlog}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* assetsValue Dropdown */}
                <Col sm={2} className="market-column">
                  <Form.Group controlId={`mappings.${index}.pimChannel`}>
                    <Form.Select
                      name={`mappings[${index}].pimChannel`}
                      value={inputField?.pimChannel}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.pimChannel &&
                        touched.mappings?.[index]?.pimChannel
                      }
                    >
                    <option value="">Select the PIM Channel</option>
                    {filteringOptions(pimChannelOptions,index,"pimChannel")?.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.pimChannel}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col
                  sm={2}
                  className="market-column"
                  className="d-flex justify-content-center "
                >
                  <button type="button" className="market_locale_button" disabled={!inputField?.catlog || !inputField?.pimChannel} onClick={()=>{setMarketLocaleIndex(index)}}>
                    {(!localStorage.getItem(`${storeDomain+'|'+inputField?.catlog}`) || JSON.parse(localStorage.getItem(`${storeDomain+'|'+inputField?.catlog}`))?.mappings?.length === 0 ||  (JSON.parse(localStorage.getItem(`${storeDomain+'|'+inputField?.catlog}`))?.mappings?.length === 1 && JSON.parse(localStorage.getItem(`${storeDomain+'|'+inputField?.catlog}`))?.mappings?.[0]?.pimlocale === "" && JSON.parse(localStorage.getItem(`${storeDomain+'|'+inputField?.catlog}`))?.mappings?.[0]?.shopifylocale === "")) ? "Select Locales" : " Locales selected"}
                  </button>
                </Col>
                
                {marketLocaleIndex===index && <MarketLocalePopup toggle={marketLocaleIndex} setToggle={setMarketLocaleIndex} pimLocale={pimlocaleOptions?.[inputField?.pimChannel]} shopifyLocale={shopifyLocaleOptions?.[inputField?.market]} reference={inputField?.catlog}/>}

                <Col sm={2} className="market-column">
                  <Form.Group controlId={`mappings.${index}.pimCurrency`}>
                    <Form.Select
                      name={`mappings[${index}].pimCurrency`}
                      value={inputField?.pimCurrency}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.pimCurrency &&
                        touched.mappings?.[index]?.pimCurrency
                      }
                    >
                    <option value="">Select the PIM Currency</option>
                    {pimCurrencyOptions?.[inputField?.pimChannel]?.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.pimCurrency}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Remove button */}
                <Col sm={1} className="ps-0 pt-1">
                  {values.mappings.length > 1 && (
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleRemoveFields(index,inputField.catlog)}
                      className="channelActionBtn p-0"
                    >
                      <DeleteOutlineIcon style={{ fontSize: "30px" }} />
                    </Button>
                  )}
                </Col>
              </Row>
              {index === lastIndex && (
                <Row>
                  <Col
                    sm={10}
                    className="d-flex justify-content-center mt-3 ms-4"
                  >
                    <Button
                      type="button"
                      onClick={handleAddFields}
                      style={{
                        border: "1px solid lightgray",
                        borderRadius: "7px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                      }}
                    >
                      Add new mapping
                    </Button>
                  </Col>
                </Row>
              )}
              </React.Fragment>
              ))}
              <Row className="mx-3">
                <Col sm={12}>
                  {/* <Button
                    variant="primary"
                    type="submit"
                    className="stru-btn"
                    // onSubmit={handleSubmit}
                    // disabled={isSubmitting}
                  >
                    Save Config
                  </Button> */}
                  <Button
                    variant="primary"
                    type="submit"
                    className="stru-btn"
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? 'Saving...' : 'Save Config'}
                  </Button>
                </Col>
              </Row>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Form>
    </div>
    
  );
}

export default MarketConfiguration;
